/**
 * IDDL — Research Dataset Export tab.
 *
 * Operational attempts data (candidate names, pass/fail, licences,
 * timestamps) lives on the OPERATIONAL ATTEMPTS tab. This view is the
 * research-grade counterpart: behavioural event streams, dwell/hesitation
 * timings, reversal counts, sequence analysis, and full per-attempt
 * reflection text for downstream research evidence work.
 *
 * Backend: GET /api/admin/research_export.xlsx → 4-sheet workbook.
 */
import DownloadButton from "@/components/DownloadButton";
import { Button } from "@/components/ui/button";
import { adminResearchExportXlsxUrl, adminToken } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { FileSpreadsheet, Activity, Clock, GitBranch, NotebookPen, AlertTriangle } from "lucide-react";

const SHEET_CARDS = [
  {
    id: "attempts",
    icon: Activity,
    title: "Attempts",
    body: "One row per attempt with the DS→CS→AI sequence, per-concept outcomes, knowledge/capability/reflection totals, overall outcome, duration, and licence id.",
  },
  {
    id: "knowledge",
    icon: NotebookPen,
    title: "Knowledge_Picks",
    body: "One row per (attempt × concept) with the candidate's MCQ picks (option indices), the capability outcome, and the final decision.",
  },
  {
    id: "events",
    icon: Clock,
    title: "Behaviour_Events",
    body: "One row per behaviour event from the V2 engine — full analytics: dwell_ms, hesitation_ms, interaction_count, is_required/critical/auto_fail/reversal, prev_event, config_version, config_hash. Auto-fail rows are tinted red.",
  },
  {
    id: "reflections",
    icon: GitBranch,
    title: "Reflections",
    body: "One row per (attempt × concept) with verbatim reflection text, the contract's required themes, and the themes the deterministic scorer detected.",
  },
];

export default function ResearchExport() {
  const { t } = useT();
  return (
    <div className="mt-6" data-testid="research-export-tab">
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <p className="text-sm text-[var(--iddl-muted)] leading-relaxed">
            The research dataset is a single Excel workbook with{" "}
            <strong>four cross-linked sheets</strong>, one per analytical view.
            It is intended for behaviour-analytics, timing studies, sequence
            analysis, and research evidence work — NOT for day-to-day
            operational audit (use the <em>Operational Attempts</em> tab for that).
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            {SHEET_CARDS.map((c) => {
              const Icon = c.icon;
              return (
                <div
                  key={c.id}
                  className="border border-[var(--iddl-rule)] bg-[var(--iddl-paper)] p-4"
                  data-testid={`research-sheet-${c.id}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Icon size={14} />
                    <span className="font-display font-semibold text-sm">{c.title}</span>
                  </div>
                  <div className="text-xs text-[var(--iddl-muted)] leading-relaxed">
                    {c.body}
                  </div>
                </div>
              );
            })}
          </div>
          <div
            className="border border-amber-300 bg-amber-50 p-3 text-xs flex gap-2 items-start"
            data-testid="research-export-pii-note"
          >
            <AlertTriangle size={14} className="text-amber-700 shrink-0 mt-0.5" />
            <span>
              <strong>Privacy note:</strong> the workbook includes
              candidate names, emails, IDs, and the full verbatim
              reflection text. Treat the file as confidential and
              follow your institution's data-handling policy when
              sharing. The candidate-facing app never exposes any of
              this beyond the candidate's own attempt.
            </span>
          </div>
        </div>

        <aside className="lg:col-span-1">
          <div className="border border-[var(--iddl-rule)] bg-white p-5 sticky top-6">
            <div className="label-mono mb-1">{t("admin.research_title")}</div>
            <h2 className="font-display text-xl font-semibold mb-3">
              Download research dataset
            </h2>
            <p className="text-xs text-[var(--iddl-muted)] mb-4">
              Generates the workbook live from the database. A fresh
              snapshot every time — re-run after each batch of attempts
              to capture the latest events.
            </p>
            <DownloadButton
              url={`${adminResearchExportXlsxUrl()}?admin_token=${encodeURIComponent(adminToken.get() || "")}`}
              filename="IDDL_Research_Export.xlsx"
              label="Download .xlsx"
              icon={<FileSpreadsheet size={14} className="ml-2" />}
              className="w-full"
              data-testid="btn-research-download-xlsx"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.location.reload()}
              className="rounded-none w-full mt-2"
              data-testid="btn-research-refresh"
            >
              Refresh page
            </Button>
            <div className="label-mono text-[var(--iddl-muted)] mt-4 text-xs">
              Format: .xlsx · auth: admin-token (header or query) ·
              endpoint: <code>/api/admin/research_export.xlsx</code>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
